import React, { useEffect, useState, Suspense } from "react";
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Preload } from "@react-three/drei";
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { MeshStandardMaterial, Mesh, Vector3, Box3, Color } from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';

const STLViewerQuot = ({ url, currentColor, onStatsUpdate, data, currentDencity, nanito = false, setNanito, isUpdateJob, setIsUpdateJob }) => {
  const [model, setModel] = useState(null);
  const [scale, setScale] = useState([1, 1, 1]);
  const [position, setPosition] = useState([0, 0, 0]);
  const [loadingProgress, setLoadingProgress] = useState(0);

useEffect(() => {
  if (!url) return;

  const extension = url.split('.').pop().toLowerCase();

  if (extension === 'stl') {
    const loader = new STLLoader();
    loader.load(
      url,
      (geometry) => {
        const material = new MeshStandardMaterial({
          color: new Color(currentColor || 0xCCCCCC),
          roughness: 0.3,
          metalness: 0.9
        });
        const mesh = new Mesh(geometry, material);

        const boundingBox = new Box3().setFromObject(mesh);
        const center = new Vector3();
        boundingBox.getCenter(center);
        geometry.translate(-center.x, -center.y, -center.z);

        const size = new Vector3();
        boundingBox.getSize(size);
        const maxAxis = Math.max(size.x, size.y, size.z);
        const scaleValue = 10 / maxAxis;

        setScale([scaleValue, scaleValue, scaleValue]);
        setPosition([0, 0, 0]);
        setModel(mesh);

        const volume = calculateVolume(geometry);
        const surfaceArea = calculateSurfaceArea(geometry);
        geometry.computeBoundingBox();
        const dimensions = geometry.boundingBox.getSize(new Vector3());

        const density = currentDencity === 0 ? 1 : currentDencity;

        if (currentDencity > 0) {
          try {
            if (!nanito) {
              const weight = volume * density;
              data.weight = weight;
              data.dimensions = dimensions;
              data.volume = volume;
              data.area = surfaceArea;
              const stats = { ...data };
              onStatsUpdate(stats);
            } else {
              setNanito(false);
            }
          } catch (error) {
            console.error(error);
          }
        }
      },
      (xhr) => {
        setLoadingProgress(Math.round((xhr.loaded / xhr.total) * 100));
      },
      (error) => {
        console.error('Error loading STL:', error);
      }
    );
  }

 else if (extension === 'obj') {
  const loader = new OBJLoader();
  loader.load(
    url,
    (obj) => {
      let boundingBox = new Box3().setFromObject(obj);
      let center = new Vector3();
      boundingBox.getCenter(center);
      obj.position.sub(center); 

      const size = new Vector3();
      boundingBox.getSize(size);
      const maxAxis = Math.max(size.x, size.y, size.z);
      const scaleValue = 10 / maxAxis;

      obj.traverse((child) => {
        if (child.isMesh) {
          child.material = new MeshStandardMaterial({
            color: new Color(currentColor || 0xCCCCCC),
            roughness: 0.3,
            metalness: 0.9
          });

          const geometry = child.geometry;
          if (geometry) {
            const volume = calculateVolume(geometry);
            const surfaceArea = calculateSurfaceArea(geometry);
            geometry.computeBoundingBox();
            const dimensions = geometry.boundingBox.getSize(new Vector3());
            const density = currentDencity === 0 ? 1 : currentDencity;

            if (currentDencity > 0) {
              try {
                if (!nanito) {
                  const weight = volume * density;
                  data.weight = weight;
                  data.dimensions = dimensions;
                  data.volume = volume;
                  data.area = surfaceArea;
                  const stats = { ...data };
                  onStatsUpdate(stats);
                } else {
                  setNanito(false);
                }
              } catch (error) {
                console.error(error);
              }
            }
          }
        }
      });

      setScale([scaleValue, scaleValue, scaleValue]);
      setPosition([0, 0, 0]);
      setModel(obj);
    },
    (xhr) => {
      setLoadingProgress(Math.round((xhr.loaded / xhr.total) * 100));
    },
    (error) => {
      console.error('Error loading OBJ:', error);
    }
  );
}

}, [url, currentColor]);


  const calculateVolume = (geometry) => {
    let volume = 0;
    const position = geometry.attributes.position.array;
    for (let i = 0; i < position.length; i += 9) {
      const p1 = new Vector3(position[i], position[i + 1], position[i + 2]);
      const p2 = new Vector3(position[i + 3], position[i + 4], position[i + 5]);
      const p3 = new Vector3(position[i + 6], position[i + 7], position[i + 8]);
      volume += signedVolumeOfTriangle(p1, p2, p3);
    }
    return Math.abs(volume);
  };

  const signedVolumeOfTriangle = (p1, p2, p3) => {
    return p1.dot(p2.cross(p3)) / 6.0;
  };

  const calculateSurfaceArea = (geometry) => {
    let area = 0;
    const position = geometry.attributes.position.array;
    for (let i = 0; i < position.length; i += 9) {
      const p1 = new Vector3(position[i], position[i + 1], position[i + 2]);
      const p2 = new Vector3(position[i + 3], position[i + 4], position[i + 5]);
      const p3 = new Vector3(position[i + 6], position[i + 7], position[i + 8]);
      area += triangleArea(p1, p2, p3);
    }
    return area;
  };

  const triangleArea = (p1, p2, p3) => {
    return p2.clone().sub(p1).cross(p3.clone().sub(p1)).length() / 2.0;
  };

  return (
    <div>
      <div style={{ width: '100%', height: "53vh", position: 'relative' }}>

        <Canvas
          frameloop='demand'
          shadows
          camera={{ position: [25, 25, 25], fov: 35 }} 
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: '100%',
            height: '100%',
            border: '1px solid #dcdcdc',
            padding: "20px",
          }}>
          <Suspense fallback={null}>
            <ambientLight intensity={5} />
            <directionalLight position={[10, 10, 10]} intensity={1} castShadow />
            <directionalLight position={[-10, -10, -10]} intensity={0.5} castShadow />
            {model && (
              <primitive
                object={model}
                scale={scale}
                position={position}
                rotation={[4.7, 0, 0]}
              />
            )}
            <OrbitControls enableZoom={true} autoRotate={false} target={[0, 0, 0]} />
            <Preload all />
          </Suspense>
        </Canvas>

       
        {!model && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#555',
            fontFamily: "'Arial Black', Gadget, sans-serif",
            fontSize: '15px',
            textAlign: 'center',
            padding: '10px',
            borderRadius: '8px'
          }}>
            <p>{loadingProgress}%</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default STLViewerQuot;
